
  import { defineComponent } from 'vue';
  import { mapActions, mapGetters } from 'vuex';
  import axios from 'axios';

  import Constants from '@/common/constants';

  import { USER_GROUPS_MESSAGE } from '../../constants/alert';
  import { FTS_WEEKLY_ACTIONS_NAME } from '../../store/ftsWeekly/name';
  import { USER_GROUPS_ACTIONS_NAME, USER_GROUPS_GETTERS_NAME } from '../../store/userGroups/name';
  import { TCustomsOrgansOnBorderItem } from '../../types/common-aggregated';
  import {
    TUserGroupListsModalSaveGroupItemsParamsTransport,
    TUserGroupListsModalTransportGroup,
    TUserGroupListsModalTransportSaveData,
  } from '../../types/user-group-lists-modal';
  import { collectSubtreeIds, findNodeById } from '../../utils/fts/user-groups/tree-helpers';
  import BaseListsModal from './BaseListsModal.vue';
  import { TUserGroupListConfig } from './list/types';
  import AddEditTreeGroupModal from './modals/AddEditTreeGroupModal.vue';

  export default defineComponent({
    name: 'UserTransportListsModal',
    components: { BaseListsModal, AddEditTreeGroupModal },
    props: { showModal: { type: Boolean, default: false } },
    emits: ['close', 'deletedSubtreeIds'],
    data(): {
      selectedGroup: number | null;
      selectedItems: number[];
      showAddEditModal: boolean;
      editingGroup: TCustomsOrgansOnBorderItem | null;
      loading: boolean;
      loadingLinks: boolean;
      rootGroupId: number | null;
      linksAbortController: AbortController | null;
    } {
      return {
        selectedGroup: null,
        selectedItems: [],
        showAddEditModal: false,
        editingGroup: null,
        loading: false,
        loadingLinks: false,
        rootGroupId: null,
        linksAbortController: null,
      };
    },
    computed: {
      ...mapGetters({
        transportGroups: USER_GROUPS_GETTERS_NAME.getTransportGroups,
        transportItems: USER_GROUPS_GETTERS_NAME.getTransportItems,
      }),
      leftConfig(): TUserGroupListConfig {
        return {
          items: this.transportGroups,
          viewMode: 'treeSingle',
          listId: 'left-transport-groups',
          loading: this.loading,
          showExpandControls: this.hasExpandableLeftGroups,
          key: 'left-transport-groups',
        };
      },
      rightConfig(): TUserGroupListConfig {
        return {
          items: this.transportItems,
          viewMode: 'flatMultiple',
          listId: 'right-tree',
          loading: this.loading || this.loadingLinks,
          key: this.transportItems.length,
        };
      },
      isRootSelected(): boolean {
        return this.selectedGroup === -1;
      },
      hasExpandableLeftGroups(): boolean {
        const hasChildren = (groups: TUserGroupListsModalTransportGroup[]): boolean =>
          groups.some((g) => (g.children?.length ?? 0) > 0);
        return hasChildren(this.transportGroups);
      },
      transformedGroupsTree(): TCustomsOrgansOnBorderItem[] {
        const transform = (nodes: TUserGroupListsModalTransportGroup[]): TCustomsOrgansOnBorderItem[] => {
          return nodes.map((node) => ({
            id: node.id,
            name: node.name,
            kod: node.code,
            parent: node.parent,
            children: node.children ? transform(node.children) : undefined,
          }));
        };
        return transform(this.transportGroups);
      },
    },
    created() {
      this.loadData();
    },
    watch: {
      selectedGroup: {
        async handler(newGroupId: number | null) {
          if (!newGroupId) {
            this.selectedItems = [];
            return;
          }
          await this.loadLinksForGroup(newGroupId);
        },
        immediate: true,
      },
    },
    methods: {
      ...mapActions({
        searchTransportGroups: USER_GROUPS_ACTIONS_NAME.searchTransportGroups,
        searchTransportItems: USER_GROUPS_ACTIONS_NAME.searchTransportItems,
        searchTransportLinks: USER_GROUPS_ACTIONS_NAME.searchTransportLinks,
        createGroupAction: USER_GROUPS_ACTIONS_NAME.createTransportGroup,
        updateGroupAction: USER_GROUPS_ACTIONS_NAME.updateTransportGroup,
        deleteGroupAction: USER_GROUPS_ACTIONS_NAME.deleteTransportGroup,
        saveGroupItemsAction: USER_GROUPS_ACTIONS_NAME.saveTransportGroupItems,
        searchTransportTypes: FTS_WEEKLY_ACTIONS_NAME.searchTransportTypes,
      }),

      onUpdateLeft(value: number | null) {
        this.selectedGroup = value !== null ? Number(value) : null;
      },
      onUpdateRight(value: number[] | null) {
        this.selectedItems = value ? value.map(Number) : [];
      },

      getFirstGroupId(): number | null {
        if (this.transportGroups.length === 0) return null;
        const firstNonRoot = this.transportGroups.find((g: TUserGroupListsModalTransportGroup) => g.parent !== -1);
        return firstNonRoot ? firstNonRoot.id : null;
      },

      async loadData(): Promise<void> {
        this.loading = true;
        try {
          const [groupsResult, itemsResult] = await Promise.all([
            this.searchTransportGroups(),
            this.searchTransportItems(),
          ]);

          if (!groupsResult?.success) {
            Constants.alert.fire({
              icon: 'error',
              title: USER_GROUPS_MESSAGE.TITLE_ERROR,
              text: USER_GROUPS_MESSAGE.COMMON.GET_GROUPS_FAILED,
            });
          }

          if (!itemsResult?.success) {
            Constants.alert.fire({
              icon: 'error',
              title: USER_GROUPS_MESSAGE.TITLE_ERROR,
              text: USER_GROUPS_MESSAGE.TRANSPORT.GET_ITEMS_FAILED,
            });
          }

          const findRoot = (groups: TUserGroupListsModalTransportGroup[]): number | null => {
            for (const g of groups) {
              if (g.parent === -1) return g.id;
              if (g.children) {
                const found = findRoot(g.children);
                if (found) return found;
              }
            }
            return null;
          };
          this.rootGroupId = findRoot(this.transportGroups);

          this.selectedGroup = this.getFirstGroupId();
        } finally {
          this.loading = false;
        }
      },

      async loadLinksForGroup(groupId: number): Promise<void> {
        if (this.linksAbortController) {
          this.linksAbortController.abort();
        }
        const abortController = new AbortController();
        this.linksAbortController = abortController;

        this.loadingLinks = true;

        try {
          const result = await this.searchTransportLinks({ groupId, abortController });
          if (!result?.success && !abortController.signal.aborted) {
            Constants.alert.fire({
              icon: 'error',
              title: USER_GROUPS_MESSAGE.TITLE_ERROR,
              text: USER_GROUPS_MESSAGE.COMMON.LINKS_LOAD_FAILED,
            });
          }
          if (!result?.success) return;
          this.selectedItems = result.data?.map((item: TUserGroupListsModalTransportGroup) => item.id) ?? [];
        } finally {
          this.loadingLinks = false;
          if (this.linksAbortController === abortController) {
            this.linksAbortController = null;
          }
        }
      },

      handleAdd(): void {
        this.editingGroup = null;
        this.showAddEditModal = true;
      },
      handleEdit(): void {
        if (this.isRootSelected || !this.selectedGroup) return;
        const node = findNodeById(this.transportGroups, this.selectedGroup);
        if (node) {
          this.editingGroup = {
            id: node.id,
            name: node.name,
            kod: node.code || '',
            parent: node.parent,
            children: undefined,
          };
          this.showAddEditModal = true;
        }
      },
      async handleDelete(): Promise<void> {
        if (!this.selectedGroup || this.isRootSelected) return;
        this.loading = true;
        try {
          const subtreeIds = collectSubtreeIds(this.transportGroups, this.selectedGroup);
          const result = await this.deleteGroupAction(this.selectedGroup);
          if (!result?.success) throw new Error();
          this.$emit('deletedSubtreeIds', subtreeIds);
          await Promise.all([this.searchTransportGroups(), this.searchTransportItems()]);
          this.selectedGroup = this.getFirstGroupId();
          Constants.alert.fire({
            icon: 'success',
            title: USER_GROUPS_MESSAGE.TITLE_SUCCESS,
            text: USER_GROUPS_MESSAGE.COMMON.DELETE_GROUP_SUCCESS,
          });
          this.searchTransportTypes();
        } catch {
          Constants.alert.fire({
            icon: 'error',
            title: USER_GROUPS_MESSAGE.TITLE_ERROR,
            text: USER_GROUPS_MESSAGE.COMMON.DELETE_GROUP_FAILED,
          });
        } finally {
          this.loading = false;
        }
      },

      async handleSaveGroup(payload: TCustomsOrgansOnBorderItem): Promise<void> {
        this.loading = true;
        try {
          const transportPayload: TUserGroupListsModalTransportSaveData = {
            id: payload.id,
            name: payload.name,
            code: payload.kod,
            parent: payload.parent,
          };

          if (payload.id) {
            const result = await this.updateGroupAction(transportPayload);
            if (!result?.success) throw new Error();
            await Promise.allSettled([this.searchTransportGroups(), this.searchTransportItems()]);
            Constants.alert.fire({
              icon: 'success',
              title: USER_GROUPS_MESSAGE.TITLE_SUCCESS,
              text: USER_GROUPS_MESSAGE.COMMON.UPDATE_GROUP_SUCCESS,
            });
          } else {
            const result = await this.createGroupAction(transportPayload);
            if (!result?.success) throw new Error();
            await Promise.allSettled([this.searchTransportGroups(), this.searchTransportItems()]);
            this.selectedGroup = result.data.id;
            Constants.alert.fire({
              icon: 'success',
              title: USER_GROUPS_MESSAGE.TITLE_SUCCESS,
              text: USER_GROUPS_MESSAGE.COMMON.CREATE_GROUP_SUCCESS,
            });
          }
          this.searchTransportTypes();
        } catch (error: unknown) {
          let errorMessage = payload.id
            ? USER_GROUPS_MESSAGE.COMMON.UPDATE_GROUP_FAILED
            : USER_GROUPS_MESSAGE.COMMON.CREATE_GROUP_FAILED;
          if (axios.isAxiosError(error)) errorMessage = error.message || errorMessage;
          Constants.alert.fire({
            icon: 'error',
            title: USER_GROUPS_MESSAGE.TITLE_ERROR,
            text: errorMessage,
          });
        } finally {
          this.loading = false;
          this.showAddEditModal = false;
        }
      },

      async handleSave(): Promise<void> {
        if (this.isRootSelected) {
          Constants.alert.fire({
            icon: 'warning',
            title: USER_GROUPS_MESSAGE.TITLE_ERROR,
            text: USER_GROUPS_MESSAGE.COMMON.ROOT_GROUP_SAVE_FORBIDDEN,
          });
          return;
        }
        if (!this.selectedGroup) {
          Constants.alert.fire({
            icon: 'warning',
            title: USER_GROUPS_MESSAGE.TITLE_ERROR,
            text: USER_GROUPS_MESSAGE.COMMON.NO_GROUP_SELECTED,
          });
          return;
        }
        this.loading = true;
        try {
          const params: TUserGroupListsModalSaveGroupItemsParamsTransport = {
            groupId: this.selectedGroup,
            transportTypeCodes: this.selectedItems.map((id) => String(id)),
          };
          const result = await this.saveGroupItemsAction(params);
          if (!result?.success) throw new Error();
          Constants.alert.fire({
            icon: 'success',
            title: USER_GROUPS_MESSAGE.TITLE_SUCCESS,
            text: USER_GROUPS_MESSAGE.COMMON.SAVE_LINKS_SUCCESS,
          });
          this.searchTransportTypes();
        } catch {
          Constants.alert.fire({
            icon: 'error',
            title: USER_GROUPS_MESSAGE.TITLE_ERROR,
            text: USER_GROUPS_MESSAGE.COMMON.SAVE_LINKS_FAILED,
          });
        } finally {
          this.loading = false;
        }
      },
    },
  });
